/**
 * Intent-preload of the code-split Stage canvas (#346, ADR-0052 accent #2). The whole
 * of three/drei/postprocessing lives behind the lazy `StageHost` import; on the
 * `/` → `/upload` warm dissolve that chunk must already be in flight by the time the
 * canvas mounts, or the dissolve lands on the `StageSurround` gradient and the real
 * studio "pops" in late. So every `/upload`-bound CTA warms it on pointer/focus intent.
 *
 * The core contract is **idempotence**: N hover/focus/touchstart events fire the
 * underlying dynamic `import()` AT MOST ONCE (the module registry would dedup a repeat
 * anyway, but memoizing the promise keeps the intent handlers allocation-free and makes
 * the "single fetch" observable). The importer is injectable so the unit test never
 * pulls the real three.js chunk.
 *
 * This module is three-FREE: the `import()` is dynamic, so importing it into the landing
 * CTAs does NOT pull three into the landing bundle (ADR-0040 §4 stays intact).
 */

/**
 * Wrap a dynamic import in a once-only, promise-memoized preloader. A SUCCESSFUL import
 * is cached forever (the whole point — one fetch); a REJECTED one clears the cache so the
 * next call retries. Without that reset a transient intent-time failure (a network blip
 * on hover) would poison the memo, and `Layout`'s `lazy(preloadStageHost)` would then
 * hand React the same rejected promise at click — the Stage would never load until a full
 * reload, even though a fresh import would now succeed.
 */
export function createPreloader<T>(importer: () => Promise<T>): () => Promise<T> {
  let promise: Promise<T> | undefined;
  return () => {
    promise ??= importer().catch((error) => {
      promise = undefined;
      throw error;
    });
    return promise;
  };
}

/**
 * The single shared StageHost preloader. `Layout`'s `React.lazy` calls the SAME memoized
 * function, so an intent-warmed chunk and the eventual mount resolve to ONE network
 * fetch — and if it is already resolved at mount there is no Suspense fallback flash.
 */
export const preloadStageHost = createPreloader(
  () => import("@/features/viewer/components/StageHost"),
);

/**
 * Intent handlers to spread onto every `/upload`-bound CTA (`DropPhotoCta`, the nav's
 * `UploadNavLink`). Hover, keyboard focus, and the first touch each warm the chunk;
 * the memoized preloader collapses them to a single fetch. Fire-and-forget — the click
 * navigation, not this preload, is what actually renders the Stage.
 */
export const stageIntentPreloadProps = {
  onMouseEnter: () => {
    void preloadStageHost();
  },
  onFocus: () => {
    void preloadStageHost();
  },
  onTouchStart: () => {
    void preloadStageHost();
  },
} as const;
